import { useState, useRef, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import {
	BsExclamationTriangle,
	BsExclamationTriangleFill,
	BsCheckCircle,
	BsPlus,
	BsFire,
	BsCircle,
	BsClock,
	BsCalendarCheck,
} from 'react-icons/bs';

import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

import { HOT_DEAL_PROBABILITY_MIN } from '../../../constants/dealThresholds';
import { useCompleteActivity } from '../../../models/activities/mutations';
import { useActivitiesByDeal } from '../../../models/activities/queries';
import { useChannelTypes } from '../../../models/channels/queries';
import { resolveChannelIcon } from '../../../utils/channelIcons';
import { isCallActivity } from '../../../utils/isCallActivity';
import CallReachedPopover from '../../common/CallReachedConfirm/CallReachedPopover';

function formatDueLabel(iso) {
	if (!iso) return null;
	const due = new Date(iso);
	const diffDays = Math.round((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
	if (diffDays < 0) return `${Math.abs(diffDays)} Tag${Math.abs(diffDays) !== 1 ? 'e' : ''} überfällig`;
	if (diffDays === 0) return 'Heute fällig';
	if (diffDays === 1) return 'Morgen fällig';
	return `Fällig in ${diffDays} Tagen`;
}

function isDueOverdue(iso) {
	if (!iso) return false;
	return new Date(iso).getTime() < Date.now();
}

function isDueToday(iso) {
	if (!iso) return false;
	const due = new Date(iso);
	const now = new Date();
	return (
		due.getFullYear() === now.getFullYear() && due.getMonth() === now.getMonth() && due.getDate() === now.getDate()
	);
}

const STATUS_STYLES = {
	overdue: 'bg-warning-200 text-warning-700 hover:brightness-95',
	today: 'bg-success-200 text-success-700 hover:brightness-95',
	future: 'bg-bgBase-400 text-bgBase-800 hover:bg-bgBase-500',
	none: 'bg-caution-200 text-caution-700 hover:brightness-95',
};

const STATUS_FALLBACK_ICONS = {
	overdue: BsExclamationTriangleFill,
	today: BsCalendarCheck,
	future: BsClock,
	none: BsExclamationTriangle,
};

function getActivityStatus({ overdueCount, plannedCount, nextActivityDueAt }) {
	if (overdueCount > 0) return 'overdue';
	if (nextActivityDueAt && isDueToday(nextActivityDueAt)) return 'today';
	if (plannedCount > 0) return 'future';
	return 'none';
}

function getStatusTitle(variant, { overdueCount, plannedCount, nextActivityDueAt }) {
	if (variant === 'none') return 'Keine Aktivität geplant';

	const countLabel =
		variant === 'overdue'
			? `${overdueCount} überfällige Aktivität${overdueCount !== 1 ? 'en' : ''}`
			: `${plannedCount} geplante Aktivität${plannedCount !== 1 ? 'en' : ''}`;

	// Append the next activity's due date (e.g. "Fällig in 3 Tagen") when available
	const dueLabel = formatDueLabel(nextActivityDueAt);
	return dueLabel ? `${countLabel} · ${dueLabel}` : countLabel;
}

const ActivityRow = ({
	activity,
	channelTypes,
	companyName,
	callReachedActiveId,
	onCheckboxClick,
	onCallReachedSelect,
	onCallReachedCancel,
	registerRef,
}) => {
	const channel = channelTypes.find((c) => c.id === activity.channelTypeId);
	const ChannelIcon = channel ? resolveChannelIcon(channel) : BsCircle;
	const overdue = isDueOverdue(activity.dueAt);
	const isCallReachedOpen = callReachedActiveId === activity.id;

	return (
		<div className="flex items-start gap-2 px-3 py-2 hover:bg-bgBase-300 transition-colors border-b border-bgBase-400 last:border-b-0">
			{activity.isAutomated ? (
				<span
					className="mt-0.5 flex-shrink-0 text-[8px] font-semibold bg-primary-200 text-primary-700 px-1 py-0.5 rounded-full leading-none"
					title="Wird automatisch ausgeführt"
				>
					Auto
				</span>
			) : (
				<>
					<button
						type="button"
						ref={(el) => registerRef(activity.id, el)}
						onClick={(e) => {
							e.stopPropagation();
							onCheckboxClick(activity);
						}}
						className="mt-0.5 flex-shrink-0 h-4 w-4 rounded border border-bgBase-600 hover:border-primary-400 hover:bg-primary-100 flex items-center justify-center transition-colors group"
						title="Als erledigt markieren"
					>
						<BsCheckCircle className="w-2.5 h-2.5 text-transparent group-hover:text-primary-400" />
					</button>
					{isCallReachedOpen && (
						<CallReachedPopover
							anchorRef={{ current: registerRef.read(activity.id) }}
							onSelect={(reached) => onCallReachedSelect(activity.id, reached)}
							onCancel={onCallReachedCancel}
						/>
					)}
				</>
			)}
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-1.5">
					<ChannelIcon className="w-3 h-3 flex-shrink-0 text-bgBase-800" />
					<span className="text-xs font-medium text-text truncate">{activity.title}</span>
				</div>
				<div className="text-[10px] text-bgBase-800 mt-0.5 truncate">
					{activity.dueAt && (
						<span className={overdue ? 'text-warning-500 font-semibold' : ''}>
							{formatDueLabel(activity.dueAt)}
						</span>
					)}
					{activity.dueAt && companyName && <span> · </span>}
					{companyName && <span>{companyName}</span>}
				</div>
			</div>
		</div>
	);
};

ActivityRow.propTypes = {
	activity: PropTypes.object.isRequired,
	channelTypes: PropTypes.array.isRequired,
	companyName: PropTypes.string,
	callReachedActiveId: PropTypes.string,
	onCheckboxClick: PropTypes.func.isRequired,
	onCallReachedSelect: PropTypes.func.isRequired,
	onCallReachedCancel: PropTypes.func.isRequired,
	registerRef: PropTypes.func.isRequired,
};

const ActivityPopover = ({ deal, probability }) => {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const activityCheckboxRefs = useRef({});

	const completeActivity = useCompleteActivity();
	const { data: channelTypes = [] } = useChannelTypes(true);
	const { data: allActivities = [], isLoading: isLoadingActivities } = useActivitiesByDeal(open ? deal.id : null);
	const openActivities = allActivities.filter((a) => a.status === 'planned' || a.status === 'in_progress');
	const [callReachedActivityId, setCallReachedActivityId] = useState(null);

	const plannedCount = deal.plannedActivityCount ?? 0;
	const overdueCount = deal.overdueActivityCount ?? 0;
	const isHot = deal.status === 'open' && probability > HOT_DEAL_PROBABILITY_MIN;
	const hasActivities = plannedCount > 0 || overdueCount > 0;

	const statusVariant = getActivityStatus({
		overdueCount,
		plannedCount,
		nextActivityDueAt: deal.nextActivityDueAt,
	});
	const StatusIcon = STATUS_FALLBACK_ICONS[statusVariant];
	const statusCount = overdueCount > 0 ? overdueCount : plannedCount;

	const closeMenu = useCallback(() => setOpen(false), []);

	const handleCompleteWithReached = useCallback(
		(activityId, reached) => {
			const payload = reached !== null ? { channelPayload: { reached } } : {};
			completeActivity.mutate({ id: activityId, dealId: deal.id, payload });
			setCallReachedActivityId(null);
		},
		[completeActivity, deal.id],
	);

	const handleCheckboxClick = useCallback(
		(activity) => {
			if (isCallActivity(activity, channelTypes)) {
				setCallReachedActivityId(activity.id);
			} else {
				completeActivity.mutate({ id: activity.id, dealId: deal.id });
			}
		},
		[channelTypes, completeActivity, deal.id],
	);

	const handleTriggerClick = (e) => {
		e.stopPropagation();
		if (!hasActivities && deal.crmCompanyId) {
			navigate(`/crm/companies/${deal.crmCompanyId}#activities`);
		} else {
			setOpen((p) => !p);
		}
	};

	const registerCheckboxRef = useCallback((id, el) => {
		activityCheckboxRefs.current[id] = el;
	}, []);
	registerCheckboxRef.read = (id) => activityCheckboxRefs.current[id];

	const trigger = (
		<button
			type="button"
			onClick={handleTriggerClick}
			className={`inline-flex items-center gap-1 px-2 py-1 rounded-full font-semibold transition-colors ${STATUS_STYLES[statusVariant]}`}
			title={getStatusTitle(statusVariant, {
				overdueCount,
				plannedCount,
				nextActivityDueAt: deal.nextActivityDueAt,
			})}
		>
			<StatusIcon className="w-4 h-4" />
			{hasActivities ? statusCount : <BsPlus className="w-4 h-4" />}
		</button>
	);

	return (
		<div className="flex items-center gap-1.5 text-[10px] text-bgBase-800">
			{isHot && (
				<span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-caution-200 text-caution-700 font-semibold">
					<BsFire className="w-2.5 h-2.5" /> hot
				</span>
			)}

			<Popover open={open} onClose={closeMenu} trigger={trigger} align="end">
				<Panel.Body padded={false} className="max-h-56" onClick={(e) => e.stopPropagation()}>
					{isLoadingActivities ? (
						<div className="flex items-center gap-2 px-4 py-3 text-xs text-bgBase-800">
							<span className="inline-block w-3 h-3 border-2 border-bgBase-500 border-t-primary-500 rounded-full animate-spin" />
							Lade Aktivitäten…
						</div>
					) : openActivities.length === 0 ? (
						<div className="px-4 py-3 text-xs text-bgBase-800">Keine offenen Aktivitäten</div>
					) : (
						openActivities.map((activity) => (
							<ActivityRow
								key={activity.id}
								activity={activity}
								channelTypes={channelTypes}
								companyName={deal.companyNameSnapshot}
								callReachedActiveId={callReachedActivityId}
								onCheckboxClick={handleCheckboxClick}
								onCallReachedSelect={handleCompleteWithReached}
								onCallReachedCancel={() => setCallReachedActivityId(null)}
								registerRef={registerCheckboxRef}
							/>
						))
					)}
				</Panel.Body>
				<Panel.Footer className="p-0">
					<button
						type="button"
						onClick={() => {
							setOpen(false);
							if (deal.crmCompanyId) navigate(`/crm/companies/${deal.crmCompanyId}#activities`);
						}}
						className="w-full text-left px-3 py-2 text-xs text-primary-600 font-medium hover:bg-bgBase-300 flex items-center gap-1.5"
					>
						<BsPlus className="w-4 h-4" />
						Aktivität planen
					</button>
				</Panel.Footer>
			</Popover>
		</div>
	);
};

ActivityPopover.propTypes = {
	deal: PropTypes.object.isRequired,
	probability: PropTypes.number,
};

export default ActivityPopover;
